<template>
  <transition name="mn-slide" mode="out-in">
    <!-- Carte flottante : visible sans masquer la page ni bloquer la saisie -->
    <section
      v-if="announcement && !minimized"
      key="card"
      class="mn-card"
      :class="`is-${phase}`"
      role="region"
      aria-label="Annonce de mise à jour du site"
    >
      <div class="mn-ring" aria-hidden="true">
        <svg viewBox="0 0 44 44">
          <circle class="mn-ring-track" cx="22" cy="22" r="19" />
          <circle
            class="mn-ring-value"
            cx="22"
            cy="22"
            r="19"
            :stroke-dasharray="RING_LENGTH"
            :stroke-dashoffset="RING_LENGTH * (1 - remainingRatio)"
          />
        </svg>
        <i :class="copy.icon"></i>
      </div>

      <div class="mn-body">
        <div class="mn-head">
          <span class="mn-title">{{ copy.title }}</span>
          <span class="mn-countdown">{{ phase === 'overdue' ? 'Imminent' : countdownLabel }}</span>
        </div>
        <p class="mn-text">{{ copy.text }}</p>
        <p v-if="announcement.message" class="mn-message">
          <i class="bi bi-chat-left-text" aria-hidden="true"></i>
          {{ announcement.message }}
        </p>
      </div>

      <button
        type="button"
        class="mn-minimize"
        title="Réduire"
        aria-label="Réduire l'annonce"
        @click="setMinimized(true)"
      >
        <i class="bi bi-dash-lg"></i>
      </button>
    </section>

    <!-- Version réduite : juste le compte à rebours, un clic pour rouvrir -->
    <button
      v-else-if="announcement"
      key="pill"
      type="button"
      class="mn-pill"
      :class="`is-${phase}`"
      :title="copy.title"
      aria-label="Afficher l'annonce de mise à jour"
      @click="setMinimized(false)"
    >
      <span class="mn-pill-dot" aria-hidden="true"></span>
      <i class="bi bi-cloud-arrow-up-fill" aria-hidden="true"></i>
      <span>{{ phase === 'overdue' ? 'Mise à jour imminente' : `Mise à jour · ${countdownLabel}` }}</span>
    </button>
  </transition>

  <!-- Le compte à rebours change chaque seconde : on n'annonce aux lecteurs d'écran que les changements d'étape -->
  <span class="visually-hidden" aria-live="polite">{{ liveMessage }}</span>
</template>

<script>
import { useMaintenanceNotice } from '../composables/useMaintenanceNotice'

const RING_LENGTH = 2 * Math.PI * 19
const MINIMIZED_KEY = 'porteo_maintenance_notice_minimized'
const PHASE_RANK = { info: 0, warning: 1, critical: 2, overdue: 3 }

const COPY = {
  info: {
    icon: 'bi bi-cloud-arrow-up-fill',
    title: 'Mise à jour du site prévue',
    text: 'Le site sera brièvement indisponible. Pensez à enregistrer votre travail avant la fin du compte à rebours.'
  },
  warning: {
    icon: 'bi bi-exclamation-triangle-fill',
    title: 'Mise à jour dans moins de 5 minutes',
    text: 'Enregistrez dès maintenant vos modifications en cours.'
  },
  critical: {
    icon: 'bi bi-exclamation-octagon-fill',
    title: 'Mise à jour dans moins d\'une minute',
    text: 'Enregistrez immédiatement : le site va être interrompu.'
  },
  overdue: {
    icon: 'bi bi-arrow-repeat',
    title: 'Mise à jour en cours de lancement',
    text: 'Le site peut être interrompu à tout moment. Enregistrez vos modifications.'
  }
}

const readMinimized = () => {
  try {
    return sessionStorage.getItem(MINIMIZED_KEY)
  } catch {
    return null
  }
}

const writeMinimized = (value) => {
  try {
    if (value) sessionStorage.setItem(MINIMIZED_KEY, value)
    else sessionStorage.removeItem(MINIMIZED_KEY)
  } catch {
    // Stockage indisponible : la préférence ne survit simplement pas au rechargement
  }
}

export default {
  name: 'MaintenanceNoticeBanner',
  setup() {
    const { announcement, phase, remainingRatio, countdownLabel, startPolling } = useMaintenanceNotice()
    startPolling()
    return { announcement, phase, remainingRatio, countdownLabel, RING_LENGTH }
  },
  data() {
    return {
      // id de l'annonce réduite par l'utilisateur, et étape atteinte à ce moment-là
      minimizedFor: readMinimized(),
      liveMessage: ''
    }
  },
  computed: {
    copy() {
      return COPY[this.phase] || COPY.info
    },
    minimized() {
      return !!this.announcement && this.minimizedFor === `${this.announcement.id}:${this.phase}`
    }
  },
  watch: {
    // Réduire vaut pour l'étape en cours : quand l'échéance se rapproche, la carte
    // se rouvre d'elle-même pour que personne ne rate l'avertissement
    phase(next, prev) {
      if (!next) {
        this.liveMessage = ''
        return
      }
      if (!prev || PHASE_RANK[next] > PHASE_RANK[prev]) {
        this.liveMessage = `${COPY[next].title}. ${COPY[next].text}`
      }
    },
    announcement(next) {
      if (!next) this.setMinimized(false)
    }
  },
  methods: {
    setMinimized(value) {
      this.minimizedFor = value && this.announcement ? `${this.announcement.id}:${this.phase}` : null
      writeMinimized(this.minimizedFor)
    }
  }
}
</script>

<style scoped>
.mn-card,
.mn-pill {
  --mn-accent: #818cf8;
  --mn-accent-soft: rgba(129, 140, 248, 0.18);
  position: fixed;
  z-index: 1090; /* au-dessus des modales Bootstrap (1055), sous les toasts */
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #f8fafc;
  background: rgba(15, 23, 42, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 18px 40px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px var(--mn-accent-soft);
}

.is-warning { --mn-accent: #f59e0b; --mn-accent-soft: rgba(245, 158, 11, 0.28); }
.is-critical,
.is-overdue { --mn-accent: #fb7185; --mn-accent-soft: rgba(251, 113, 133, 0.35); }

/* ---- Carte ---- */
.mn-card {
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  width: min(560px, calc(100vw - 24px));
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 14px 14px 16px;
  border-radius: 16px;
}

.is-critical.mn-card,
.is-overdue.mn-card {
  animation: mn-glow 2s ease-in-out infinite;
}

@keyframes mn-glow {
  0%, 100% { box-shadow: 0 18px 40px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px var(--mn-accent-soft); }
  50% { box-shadow: 0 18px 40px -12px rgba(15, 23, 42, 0.45), 0 0 0 5px var(--mn-accent-soft); }
}

.mn-ring {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mn-ring svg {
  position: absolute;
  inset: 0;
  transform: rotate(-90deg);
}

.mn-ring circle {
  fill: none;
  stroke-width: 3;
}

.mn-ring-track { stroke: rgba(255, 255, 255, 0.1); }

.mn-ring-value {
  stroke: var(--mn-accent);
  stroke-linecap: round;
  transition: stroke-dashoffset 1s linear, stroke 0.4s ease;
}

.mn-ring i {
  font-size: 1.05rem;
  color: var(--mn-accent);
  transition: color 0.4s ease;
}

.is-overdue .mn-ring i {
  animation: mn-spin 1.6s linear infinite;
}

@keyframes mn-spin { to { transform: rotate(360deg); } }

.mn-body {
  flex: 1;
  min-width: 0;
}

.mn-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.mn-title {
  font-family: 'Outfit', 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.1px;
}

.mn-countdown {
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--mn-accent);
  white-space: nowrap;
  transition: color 0.4s ease;
}

.mn-text {
  margin: 3px 0 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: #cbd5e1;
}

.mn-message {
  display: flex;
  gap: 7px;
  margin: 8px 0 0;
  padding: 7px 10px;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow-wrap: anywhere;
}

.mn-message i {
  color: #94a3b8;
  margin-top: 1px;
}

.mn-minimize {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: -2px -2px 0 0;
  background: transparent;
  color: #94a3b8;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.mn-minimize:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

/* ---- Pastille réduite ---- */
.mn-pill {
  right: 20px;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.mn-pill:hover { transform: translateY(-2px); }
.mn-pill i { color: var(--mn-accent); }

.mn-pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--mn-accent);
  animation: mn-dot 1.8s ease-out infinite;
}

@keyframes mn-dot {
  0% { box-shadow: 0 0 0 0 var(--mn-accent-soft); }
  70% { box-shadow: 0 0 0 8px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
}

.mn-card :is(button):focus-visible,
.mn-pill:focus-visible {
  outline: 2px solid var(--mn-accent);
  outline-offset: 2px;
}

/* ---- Transitions ---- */
.mn-slide-enter-active,
.mn-slide-leave-active {
  transition: opacity 0.3s ease, translate 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.mn-slide-enter-from,
.mn-slide-leave-to {
  opacity: 0;
  translate: 0 16px;
}

@media (max-width: 575.98px) {
  .mn-card {
    bottom: 12px;
    padding: 12px;
    gap: 12px;
  }

  .mn-pill {
    right: 12px;
    bottom: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mn-card,
  .mn-pill,
  .mn-pill-dot,
  .mn-ring i,
  .mn-ring-value {
    animation: none !important;
    transition: none !important;
  }
}
</style>
